import { forwardRef, useId, useState } from 'react';
import { clsx } from 'clsx';

type BaseProps = {
  label: string;
  /** Suffixe fixe à droite (ex. « € »). */
  suffix?: string;
  className?: string;
  containerClassName?: string;
};

type InputProps = BaseProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'placeholder' | 'className'> & {
    multiline?: false;
  };

type TextareaProps = BaseProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder' | 'className'> & {
    multiline: true;
  };

type FloatingFieldProps = InputProps | TextareaProps;

// Types de champ qui affichent toujours un contenu (UI de date/heure) : leur
// libellé est flottant en permanence, il ne peut pas reposer « dans » le champ.
const ALWAYS_FLOAT_TYPES = new Set(['date', 'datetime-local', 'month', 'time', 'week']);

/**
 * Champ à libellé flottant.
 *
 * Le nom du champ vit À L'INTÉRIEUR de la zone (comme un placeholder). Au focus,
 * ou dès qu'un contenu est saisi, il glisse depuis cette position jusqu'à la
 * bordure haute et prend la couleur d'accent ; le contour se colore au focus.
 *
 * L'état flottant est piloté DEPUIS REACT (focus + présence de valeur), et non
 * par `:placeholder-shown` : ce pseudo-sélecteur se comporte différemment selon
 * le navigateur (Firefox/Chrome/Safari) et le type de champ (`number`…), ce qui
 * laissait certains champs figés. Ici le rendu est identique partout.
 */
const FloatingField = forwardRef<HTMLInputElement | HTMLTextAreaElement, FloatingFieldProps>(
  function FloatingField({ label, suffix, className, containerClassName, ...rest }, ref) {
    const autoId = useId();
    const [focused, setFocused] = useState(false);

    const isMultiline = 'multiline' in rest && rest.multiline === true;
    // On extrait ce qu'on doit intercepter (focus/blur, valeur, type) ; le reste
    // est réappliqué tel quel sur le champ.
    const {
      multiline: _m,
      id: idProp,
      type,
      value,
      defaultValue,
      onFocus,
      onBlur,
      ...fieldProps
    } = rest as Record<string, unknown> & {
      onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
      onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    };

    const id = (idProp as string) ?? autoId;
    const inputType = typeof type === 'string' ? type : 'text';

    const current = value ?? defaultValue;
    const hasValue = current !== undefined && current !== null && String(current).length > 0;
    const floated = focused || hasValue || ALWAYS_FLOAT_TYPES.has(inputType);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(true);
      onFocus?.(e);
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFocused(false);
      onBlur?.(e);
    };

    const shared = {
      ...fieldProps,
      id,
      value: value as never,
      defaultValue: defaultValue as never,
      onFocus: handleFocus,
      onBlur: handleBlur,
    };

    return (
      <div
        className={clsx(
          'ff',
          isMultiline && 'ff--textarea',
          floated && 'ff--float',
          focused && 'ff--focus',
          containerClassName,
        )}
      >
        {isMultiline ? (
          <textarea
            {...(shared as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            className={clsx('ff-input', className)}
          />
        ) : (
          <input
            {...(shared as React.InputHTMLAttributes<HTMLInputElement>)}
            type={inputType}
            ref={ref as React.Ref<HTMLInputElement>}
            className={clsx('ff-input', suffix && 'pr-9', className)}
          />
        )}
        <label htmlFor={id} className="ff-label">{label}</label>
        {suffix && <span className="ff-suffix">{suffix}</span>}
      </div>
    );
  },
);

export default FloatingField;
